import React, { lazy, ReactElement, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { useRouter } from 'next/router';
import find from 'lodash/find';
import { logout } from '@Hotels-at-Home/hah-fe-core/dist/store/user/thunks';
import { toggleModal, toggleShowMobileMenu } from '@Hotels-at-Home/hah-fe-core/dist/store/modal';
import { selectShowModal } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/modal';
import {
  useAnalyticsScripts,
  useGetPageClass,
  useConsentModeListener,
  useInterval,
  useFirstVisit,
  useWindowSize,
  useShowError,
  useShowNotify,
  useAuthenticated,
  useScrollRestoration,
  useUserInteractionEvent,
  useCatalogId,
} from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import {
  SM_WIDTH,
  COOKIE_CHECKOUT_START,
  COOKIE_CURRENCY_NAME,
  COOKIE_LANGUAGE_NAME,
  LanguageCode,
  HOME,
  CHECKOUT,
} from '@Hotels-at-Home/hah-fe-core/dist/constants';
import Header from '@Hotels-at-Home/hah-fe-core/dist/components/Header';
import AuthSidebar from '@Hotels-at-Home/hah-fe-core/dist/components/AuthSidebar';
import SearchDrawer from '@Hotels-at-Home/hah-fe-core/dist/components/SearchDrawer';
import ShoppingCartDrawer from '@Hotels-at-Home/hah-fe-core/dist/components/CartDrawer';
import BannerWrapper from '@Hotels-at-Home/hah-fe-core/dist/components/BannerWrapper';
import AllBanner from '@Hotels-at-Home/hah-fe-core/dist/components/accor/AllBanner';
import {
  ActionType,
  IFooter,
  INavMenu,
  ModalType,
  IGetPromotionResponse,
  UtilityIcons,
} from '@Hotels-at-Home/hah-fe-core/dist/types';

import { useDispatch, useSelector } from '~/store/hooks';
import {
  getCookie,
  getCookies,
  IConsentMode,
  removeCookie,
  setPreferredLangCookie,
  setFirstVisitCookie,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/cookies';
import Footer from '../components/layouts/Footer';
import { CurrencyDrawer, LanguageDrawer } from '@Hotels-at-Home/hah-fe-core/dist/components';

import classnames from 'classnames';

import { getHrefLinks } from '~/utils/hreflangs';
import { selectProduct } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/product';
import { selectCategory } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/category';
import { selectCurrentCurrencyCode } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/currencies';
import { getTermsAndConditionsByCurrency } from '@Hotels-at-Home/hah-fe-core/dist/utils/promotion';
import { useTranslation } from 'react-i18next';
import { useHeaderAnimation } from '~/hooks/useHeaderAnimation';

import BmsmChart from '~/ui/components/common/Banner/BmsmChart';
import Modal from '@Hotels-at-Home/hah-fe-core/dist/components/Modal';
import { CookieRestorer } from '@Hotels-at-Home/hah-fe-core/dist/utils/cookieRestorer';
import { cookieRestorerEnabled, cookieRestorerOptions } from '~/constants/siteConfig';
import { isAuthenticated } from '@Hotels-at-Home/hah-fe-core/dist/services';
import Head from 'next/head';

interface IProps {
  promotion: IGetPromotionResponse;
}

const FirstVisitModal = lazy(
  () => import('@Hotels-at-Home/hah-fe-core/dist/components/FirstVisitModal'),
);

interface IProps {
  userAgent: string;
  children: React.ReactNode;
  navMenu: INavMenu;
  footer: IFooter;
  promotion: IGetPromotionResponse;
  networkErrorStatusCode: number;
  isSafari: boolean;
}

const AppLayout = ({
  userAgent,
  children,
  navMenu,
  footer,
  promotion,
  networkErrorStatusCode,
  isSafari,
}: IProps): ReactElement => {
  useCatalogId();
  useScrollRestoration();
  const { pathname, route, push, reload, query } = useRouter();
  const [consentMode, setConsentMode] = useState<IConsentMode | null>(null);
  const [shouldIntervalBeCancelled, setShouldIntervalBeCancelled] = useState(false);
  const [isFirstVisitModalVisible, setIsFirstVisitModalVisible] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$heroSection' });

  const { userClickHandler } = useUserInteractionEvent();
  const windowSize = useWindowSize();
  const isSmallScreen = Boolean(windowSize.width) && windowSize.width <= SM_WIDTH;

  useConsentModeListener((consent: any) => {
    setConsentMode(consent);
  });

  const currencyCookie = getCookie(COOKIE_CURRENCY_NAME) as string;
  const lang = getCookie(COOKIE_LANGUAGE_NAME) as LanguageCode;

  const isLogged = isAuthenticated();

  useAnalyticsScripts();
  // TODO: remove "|| 'dev'" after NEXT_ENV is added to dev .env file

  const dispatch = useDispatch();
  const showError = useShowError();
  const showNotify = useShowNotify();
  const showModal = useSelector(selectShowModal);
  const product = useSelector(selectProduct);
  const currency = useSelector(selectCurrentCurrencyCode) ?? currencyCookie;
  const { category } = useSelector(selectCategory);

  const { t } = useTranslation('', { keyPrefix: '' });

  gsap.config({ nullTargetWarn: false });
  const cookies = getCookies();

  const cookieConsent = getCookie('CookieConsent');

  const pageClass = useGetPageClass();

  const termsAndConditionsByCurrency: string = getTermsAndConditionsByCurrency(
    promotion?.promotion_terms,
    currency,
  );

  const handleToggleModal = (modal: ModalType) => {
    dispatch(toggleModal(modal));
  };

  const onAction = (action: ActionType) => {
    let modalType;

    switch (action) {
      case ActionType.Account: {
        modalType = ModalType.Account;
        break;
      }
      case ActionType.Search: {
        modalType = ModalType.Search;
        break;
      }
      case ActionType.Language: {
        modalType = ModalType.Language;
        break;
      }
      case ActionType.Currency: {
        modalType = ModalType.Currency;
        break;
      }
      case ActionType.ShoppingCart: {
        modalType = ModalType.ShoppingCart;
        break;
      }
      default: {
        break;
      }
    }

    handleToggleModal(modalType);
  };

  const onLoginSubmitted = () => {
    handleToggleModal(null);
    reload();
  };

  const onLogout = async () => {
    dispatch(logout())
      .then(() => {
        showNotify(t('$common.$validation.logoutSuccess'));

        if (pathname === HOME) {
          // Reload on HOME page instead of push to avoid NEXT_REDUX_WRAPPER_HYDRATE store data reset
          reload();
        } else {
          push(HOME);
        }
      })
      .catch((err: any) => showError(err));
  };

  const closeHandler = () => {
    if (showModal) {
      const idString = `${showModal.toLocaleLowerCase()}-button`;
      const actionButton = document.getElementById(idString);
      if (actionButton) {
        actionButton.focus();
      }
    }
    dispatch(toggleModal(null));
    dispatch(toggleShowMobileMenu(null));
  };

  const removeCheckoutCookies = () => {
    if (!pathname.includes('checkout') && cookies[COOKIE_CHECKOUT_START]) {
      removeCookie(COOKIE_CHECKOUT_START);
    }
  };

  const { isFirstVisitModalOpened, setIsFirstVisitModalOpened, handleClose } = useFirstVisit();

  const handleScroll = () => {
    if (window.scrollY > 1000) {
      setIsFirstVisitModalVisible(true);
      window.removeEventListener('scroll', handleScroll); // Remove the event listener after triggering the modal
    }
  };

  useEffect(() => {
    // Mark as client to allow client-only rendering on specific routes
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!cookieRestorerEnabled || !isSafari) return;

    CookieRestorer(cookieRestorerOptions);

    const interval = setInterval(() => {
      CookieRestorer(cookieRestorerOptions);
    }, cookieRestorerOptions.options.interval || 5000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    removeCheckoutCookies();
  }, [pathname]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll); // Clean up the event listener on component unmount
    };
  }, []);

  const isHomepage = !route.includes('/p/') && !route.includes('/c/');
  const isCheckout = route.includes(CHECKOUT);
  const isPostsPage = route.includes('/posts');
  const isMiscPages =
    route.includes('/404') ||
    route.includes('/search') ||
    route.includes('/cart') ||
    route.includes('/help');

  const isLinensCategoryPage =
    Array.isArray(query.slug) &&
    query.slug.length === 1 &&
    (query.slug[0] === 'linens' ||
      query.slug[0] === 'linge-de-lit' ||
      query.slug[0] === 'bettwasche' ||
      query.slug[0] === 'sabanas');

  const hrefLinks = getHrefLinks(isHomepage, product, category);

  const checkWindowConsentMode = () => {
    const consentCookie = getCookie('CookieConsent') as IConsentMode;
    if (consentCookie) {
      const consent: IConsentMode = consentCookie;
      if (consent) {
        setConsentMode(consent);
        setShouldIntervalBeCancelled(true);
      }
    }
    if (
      window?.CookieConsent?.consent !== undefined &&
      Object.values(window.CookieConsent.consent).every(e => e)
    ) {
      setFirstVisitCookie(userAgent, window.CookieConsent.consent);
      setConsentMode(window.CookieConsent.consent);
      setShouldIntervalBeCancelled(true);
    }
    if (
      window?.ConsentMode?.consent !== undefined &&
      Object.values(window.ConsentMode.consent).every(e => e)
    ) {
      setConsentMode(window.ConsentMode.consent);
      setShouldIntervalBeCancelled(true);
    }
    if (
      window?.Cookiebot?.consent !== undefined &&
      Object.values(window.Cookiebot.consent).every(e => e)
    ) {
      setConsentMode(window.Cookiebot.consent);
      setShouldIntervalBeCancelled(true);
    }
  };

  useInterval(
    () => {
      checkWindowConsentMode();
    },
    shouldIntervalBeCancelled ? null : 1000,
  );

  useEffect(() => {
    if (consentMode) {
      let consentObject = consentMode;

      if (typeof consentMode === 'string') {
        const consentModeString = consentMode as unknown as string;
        // Replace single quotes with double quotes
        const validJsonString = consentModeString
          .replace(/([a-zA-Z0-9_]+):/g, '"$1":')
          .replace(/'/g, '"');

        try {
          // Parse the JSON string into an object
          const jsonObject = JSON.parse(validJsonString);

          consentObject = jsonObject;
        } catch (error) {
          console.error('Failed to parse consentMode JSON:', error);
          consentObject = {};
        }
      }

      setFirstVisitCookie(userAgent, consentMode);
      setPreferredLangCookie(userAgent, currency, lang, consentObject);
    }
  }, [consentMode]);

  useEffect(() => {
    const contentSquareScript = document.createElement('script');

    contentSquareScript.src = `/js/contentsquare.js`;
    contentSquareScript.async = true;
    document.body.appendChild(contentSquareScript);
  }, []);
  useHeaderAnimation();

  const [showBMSMModal, setShowBMSMModal] = useState(false);

  const openPopup = () => {
    setShowBMSMModal(true);
  };

  const closePopup = () => {
    setShowBMSMModal(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      openPopup();
    }
  };
  /* BMSM banners logic */

  let RenderBanners;

  const [shouldHideBanner, setShouldHideBanner] = useState(false);

  useEffect(() => {
    const promoTag = getCookie('promo-tag');
    const hideForPromoTag =
      promoTag === 'air-france-x-sofitel' || promoTag === 'accorhotels-bienvenue';
    setShouldHideBanner(hideForPromoTag);
  }, []);

  if (shouldHideBanner) {
    RenderBanners = null;
  } else if (
    (promotion?.promo_is_active && promotion?.promo_type !== 'bmsm' && !isHomepage) ||
    (promotion?.promo_is_active && promotion?.promo_type !== 'bmsm' && isMiscPages) ||
    (promotion?.promo_is_active && promotion?.promo_type !== 'bmsm' && isPostsPage)
  ) {
    RenderBanners = <BannerWrapper promotion={promotion} />;
  } else if (
    (promotion?.promo_is_active && promotion?.promo_type === 'bmsm' && !isHomepage) ||
    (promotion?.promo_is_active && promotion?.promo_type === 'bmsm' && isMiscPages) ||
    (promotion?.promo_is_active && promotion?.promo_type === 'bmsm' && isPostsPage)
  ) {
    RenderBanners = (
      <>
        <div
          role="button"
          tabIndex={0}
          aria-pressed="false"
          className="bmsm-banner-wrapper"
          onClick={openPopup}
          onKeyDown={handleKeyDown}
        >
          <BannerWrapper promotion={promotion} />
        </div>
        <Modal
          open={showBMSMModal}
          maxWidth="sm"
          className="base-banner-lightbox bmsm-lightbox"
          onClose={closePopup}
        >
          <div className="content">
            <BmsmChart />
          </div>
        </Modal>
      </>
    );
  } else {
    RenderBanners = null;
  }

  /* Ends BMSM banners logic */

  /* Special Banners logic */ // Banners should end BEFORE BMSM banners shows on 03/28.
  let RenderSpecialBanners;

  const [specialBannersState, setSpecialBannersState] = useState({
    promo_is_active: false,
    promo_type: 'special',
    promo_days_left: 0,
    promo_end: '',
  });

  useEffect(() => {
    const currentDate = new Date().toISOString().split('T')[0];
    const promoEndDate = new Date(new Date().getFullYear(), 2, 26).toISOString().split('T')[0];

    setSpecialBannersState({
      promo_is_active: currentDate < promoEndDate,
      promo_type: 'special',
      promo_days_left: 6,
      promo_end: promoEndDate,
    });
  }, []);
  if (
    !shouldHideBanner &&
    !promotion?.promo_is_active &&
    specialBannersState.promo_is_active &&
    specialBannersState.promo_type === 'special'
  ) {
    RenderSpecialBanners = (
      <a
        href={tLink('linensSateenCollection')}
        target="_blank"
        rel="noreferrer"
        className="special-banner-wrapper"
      >
        <BannerWrapper promotion={specialBannersState} />
      </a>
    );
  } else {
    RenderSpecialBanners = null;
  }

  /* Ends Special Banners logic */

  const shouldRenderBanners = !isPostsPage || isClient;

  return (
    <>
      <Head>
        <meta name="theme-color" content="#050033" />
        <meta name="p:domain_verify" content="d8f4df20bbe5d800ba31ffba3bd549fe" />
      </Head>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
      jsx-a11y/no-static-element-interactions */}
      <div
        id="app"
        onClick={userClickHandler}
        className={classnames(
          {
            safari: isSafari,
            'body-font-loaded': true,
            'heading-font-loaded': true,
            'is-logged': isLogged,
          },
          pageClass,
        )}
      >
        {!isCheckout && <AllBanner />}
        <Header
          navMenu={navMenu}
          showActions={!networkErrorStatusCode}
          onAction={onAction}
          transparent
          breakNav="auto"
          sticky
          disableStyckyAnimation
          iconOrder={[
            UtilityIcons.Language,
            UtilityIcons.Currency,
            UtilityIcons.Search,
            UtilityIcons.ShoppingCart,
            UtilityIcons.Account,
          ]}
        />
        {/* On posts pages, render banners only on the client to avoid including them in SSG */}
        {shouldRenderBanners && RenderBanners}
        {isLinensCategoryPage && shouldRenderBanners ? RenderSpecialBanners : null}
        <main id="content" className="content">
          {children}
        </main>
        <Footer
          footer={footer}
          termsAndConditions={termsAndConditionsByCurrency}
          promotion={promotion}
          // Render T&C client-side only for posts pages
          clientOnlyTC={isPostsPage}
        />
        <AuthSidebar
          isLogged={isLogged}
          isOpen={showModal === ModalType.Account}
          onLoginSubmitted={onLoginSubmitted}
          onLogout={onLogout}
          onClose={closeHandler}
        />
        <SearchDrawer isOpen={showModal === ModalType.Search} onClose={closeHandler} />
        <CurrencyDrawer isOpen={showModal === ModalType.Currency} onClose={closeHandler} />
        <LanguageDrawer
          enableNA
          enableAPAC
          hrefLinks={hrefLinks}
          isOpen={showModal === ModalType.Language}
          onClose={closeHandler}
        />
        <ShoppingCartDrawer isOpen={showModal === ModalType.ShoppingCart} onClose={closeHandler} />
        {isFirstVisitModalOpened &&
          !isSmallScreen &&
          isFirstVisitModalVisible &&
          cookieConsent &&
          !isCheckout && (
            <FirstVisitModal
              subtitle={t('$local.subscribeSubtitle')}
              successSubtitle={t('$local.subscribeSuccessSubtitle')}
              onClose={handleClose}
              privacyPolicy={find(footer?.bottomFooterLinks, { name: 'privacy-policy' })}
              enableScrollingBackground
            />
          )}
      </div>
    </>
  );
};

export default AppLayout;
